import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SectionHeading, Reveal } from './_shared';
import { useListingCounts, type CountSpec } from '../hooks/useListingCounts';
import { formatAmount } from '../../../utils/numberFormat';
import { TYPE_SCENES } from './TypeScenes';

// Property-type cards. `type` is the UPPERCASE id the /search page expects
// (see pages/search filterParamMap → propertyType.toUpperCase()), so the count
// we show is exactly what the card's link returns.
//
// Owner, 2026-10-07: the tiles (an icon in a rounded square over a label) read as
// AI-made; they now follow the action cards above: a centred ink title, one
// muted line (the live count), and a small drawing of the type at the foot of
// the card that the card's edge crops. The drawing rises in on scroll and lifts
// on hover, when a few more of its windows light up in the logo's yellow.
// Owner, same day: the cards were too big at three across, so all six share one
// row from lg (three on tablets, two on phones), with smaller drawings.
const TILES: { id: keyof typeof TYPE_SCENES; type: string; isLand?: boolean }[] = [
  { id: 'apartment', type: 'APARTMENT' },
  { id: 'villa', type: 'VILLA' },
  { id: 'land', type: 'LAND', isLand: true },
  { id: 'office', type: 'OFFICE' },
  { id: 'building', type: 'BUILDING' },
  { id: 'rest_house', type: 'REST_HOUSE' },
];

export default function BrowseByType() {
  const { t, i18n } = useTranslation(['home']);
  const isRTL = i18n.dir() === 'rtl';

  const specs: CountSpec[] = useMemo(
    () => TILES.map((tile) => ({ key: tile.id, request: { propertyType: tile.type } })),
    [],
  );
  const counts = useListingCounts(specs);

  return (
    <section className="bg-white py-12 sm:py-16">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <SectionHeading title={t('home.v2.browseType.title')} isRTL={isRTL} />
        <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-6">
          {TILES.map((tile, i) => {
            const count = counts[tile.id];
            const Drawing = TYPE_SCENES[tile.id];
            return (
              <Reveal key={tile.id} delay={i * 0.05} className="h-full">
                <Link
                  to={`/search?type=${tile.type}&listingType=all`}
                  className="group relative flex h-full flex-col overflow-hidden rounded-[16px] border border-[var(--pv-border-soft)] bg-[var(--pv-color-surface)] pt-5 text-center shadow-[0_1px_2px_rgba(16,24,40,0.04)] transition-shadow duration-300 hover:shadow-[0_20px_44px_-28px_rgba(16,24,40,0.35)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pv-color-primary)] focus-visible:ring-offset-2"
                >
                  <h3 className="px-3 font-display text-[1.0625rem] font-semibold leading-snug text-[var(--pv-color-text)] transition-colors duration-200 group-hover:text-[var(--pv-color-primary)] sm:text-[1.125rem]">
                    {t(`home.v2.browseType.types.${tile.id}`)}
                  </h3>
                  {/* The live count, or a faint bar while it loads. Nothing when it
                      cannot be had, and nothing for zero: «0 إعلان» says nothing
                      worth a line (THEME.md, the main pages, rule 4). */}
                  <p className="mt-0.5 flex min-h-[1.375rem] items-center justify-center px-3 text-[0.8125rem] tabular-nums text-[var(--pv-color-text-muted)] sm:text-[0.875rem]">
                    {typeof count === 'number' && count > 0 ? (
                      t(tile.isLand ? 'home.v2.browseType.plots' : 'home.v2.browseType.listings', {
                        n: formatAmount(count, { maximumFractionDigits: 0 }),
                      })
                    ) : count === null ? (
                      <span className="h-3 w-14 animate-pulse rounded-full bg-[var(--pv-color-surface-sunken)] motion-reduce:animate-none" aria-hidden="true" />
                    ) : null}
                  </p>
                  {/* The drawing's stage: the card's bottom edge crops the ground. */}
                  <div aria-hidden="true" className="relative mt-2 h-[88px] overflow-hidden sm:h-[96px]">
                    <Reveal delay={0.12 + i * 0.05} className="absolute inset-x-0 bottom-0">
                      <div className="flex justify-center transition-transform duration-500 ease-out group-hover:-translate-y-1.5 motion-reduce:transition-none">
                        <Drawing className="-mb-2 h-[96px] w-[154px] text-[var(--pv-color-primary)] sm:h-[104px] sm:w-[166px]" />
                      </div>
                    </Reveal>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
